import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  associateManyJobs,
  getAssociationStatusConfigurationsForJobs,
  getInitialShortListedPhaseFn,
  getAssociationStatusConfigurationsShortListFn,
  associateCandidates,
} from '@modules/assigns/store';
import {
  Association,
  AssociationStatusConfiguration,
  AssociationStatusUtils,
} from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class AssignToJobService {
  constructor(private store: Store<{}>) {}

  private buildNewAssociation(
    association,
    phase: string,
    state: string,
    comment: string,
  ): Association {
    return {
      ...association,
      status: phase,
      noteComment: comment,
      hasNote: true,
      subStatus: state,
    } as Association;
  }

  addAssociations(
    associations: Association[],
    phase: string,
    state: string,
    comment: string,
    reasons = null,
    refreshJobs = false,
    outputActions = [],
  ) {
    const tempAssociations = [];
    associations.forEach((association) =>
      tempAssociations.push(this.buildNewAssociation(association, phase, state, comment)),
    );

    this.dispatchAssociations(tempAssociations, reasons, refreshJobs, outputActions);
  }

  addMultipleAssociation(jobIds: number[], candidateId: number, comment: string) {
    combineLatest([this.store.select(getAssociationStatusConfigurationsForJobs, { jobIds })])
      .pipe(
        map(([configurations]: [{ [_: string]: AssociationStatusConfiguration[] }]) =>
          jobIds.map((id) => {
            const configJobId = id;
            const initialStep = getInitialShortListedPhaseFn(configurations[configJobId]);
            const phases = getAssociationStatusConfigurationsShortListFn(
              configurations[configJobId],
            );
            const step = AssignToJobService.getInitialStepId(initialStep);
            const association = this.buildNewAssociation(
              {
                candidateId,
                reqId: id,
              },
              initialStep.id,
              step,
              comment,
            );
            association.associationStatuses =
              AssociationStatusUtils.buildVisitedAssociationStatuses(association, []);
            return association;
          }),
        ),
        take(1),
      )
      .subscribe((associations) => this.store.dispatch(associateManyJobs({ associations })));
  }

  private static getInitialStepId(initialPhase: AssociationStatusConfiguration) {
    const filteredChildren = initialPhase.children.filter((child) => !child.isRejectedState);
    return filteredChildren?.length > 0 ? filteredChildren[0].id : null;
  }

  private dispatchAssociations(
    associations: Association[],
    reasons = null,
    refreshJobs = false,
    outputActions = [],
  ) {
    const tempAssociations = [];
    associations.forEach((association) => {
      tempAssociations.push({
        ...association,
        associationStatuses: AssociationStatusUtils.buildVisitedAssociationStatuses(
          association,
          association.associationStatuses,
          reasons,
        ),
      });
    });
    this.store.dispatch(
      associateCandidates({
        associations: tempAssociations,
        totalMatchesAssign: refreshJobs,
        actions: outputActions,
        batchOperation: true,
      }),
    );
  }
}
